import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Users, DollarSign, Clock, Copy, Check, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReferrerDashboard = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/referrers/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch referrer data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const copyCode = () => {
    if (data?.referral_code) {
      navigator.clipboard.writeText(data.referral_code);
      setCopied(true);
      toast.success('Codigo copiado');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyLink = () => {
    if (data?.referral_code) {
      const link = `${window.location.origin}/register?ref=${data.referral_code}`;
      navigator.clipboard.writeText(link);
      toast.success('Link de referido copiado');
    }
  };

  const typeLabels = { general: 'General', influencer: 'Influencer', vendedor: 'Vendedor' };

  if (loading) return (
    <div className="min-h-screen bg-[#F8FAF9]">
      <Navbar />
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00CC66]" />
      </div>
    </div>
  );

  if (!data) return null;

  return (
    <div className="min-h-screen bg-[#F8FAF9]" data-testid="referrer-dashboard">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:py-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#064E3B]" data-testid="referrer-name">
              Hola, {data.name}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="border-[#00CC66] text-[#00CC66]">
                {typeLabels[data.referrer_type] || data.referrer_type}
              </Badge>
              <Badge className="bg-[#064E3B] text-white">
                {data.commission_percentage}% comision
              </Badge>
            </div>
          </div>
          {/* Referral code box */}
          <Card className="bg-white border-2 border-dashed border-[#00CC66] shadow-none">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-gray-500 mb-1">Tu codigo de referido</p>
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold text-lg text-[#064E3B]" data-testid="referral-code">
                  {data.referral_code}
                </span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={copyCode} data-testid="copy-code-btn">
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-400" />}
                </Button>
              </div>
              <Button variant="ghost" size="sm" className="mt-1 text-xs text-[#00CC66]" onClick={copyLink} data-testid="copy-link-btn">
                <Link2 className="w-3 h-3 mr-1" /> Copiar link de referido
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card data-testid="stat-referrals">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#064E3B]">{data.total_referrals}</p>
                <p className="text-sm text-gray-500">Proveedores referidos</p>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-earned">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#064E3B]">${data.total_earned?.toLocaleString('es-CL')}</p>
                <p className="text-sm text-gray-500">Total ganado</p>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-pending">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-yellow-50 flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#064E3B]">${data.total_pending?.toLocaleString('es-CL')}</p>
                <p className="text-sm text-gray-500">Pendiente de pago</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Referrals table */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg text-[#064E3B]">Mis Proveedores Referidos</CardTitle>
          </CardHeader>
          <CardContent>
            {data.referrals?.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>Aun no tienes proveedores referidos</p>
                <p className="text-sm mt-1">Comparte tu codigo <strong>{data.referral_code}</strong> para comenzar</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="referrals-table">
                  <thead>
                    <tr className="border-b text-left text-gray-500">
                      <th className="pb-2 font-medium">Proveedor</th>
                      <th className="pb-2 font-medium">Plan</th>
                      <th className="pb-2 font-medium">Precio plan</th>
                      <th className="pb-2 font-medium">Meses gratis</th>
                      <th className="pb-2 font-medium">Comision mensual</th>
                      <th className="pb-2 font-medium">Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.referrals.map((ref) => (
                      <tr key={ref.id} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="py-3 font-medium text-[#064E3B]">{ref.provider_name}</td>
                        <td className="py-3">
                          <Badge variant="outline">{ref.plan_name}</Badge>
                        </td>
                        <td className="py-3">${ref.plan_price?.toLocaleString('es-CL')}</td>
                        <td className="py-3">
                          {ref.free_months_remaining > 0 ? (
                            <Badge className="bg-yellow-100 text-yellow-700">{ref.free_months_remaining} meses</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700">Activo</Badge>
                          )}
                        </td>
                        <td className="py-3 font-semibold text-green-600">
                          ${ref.monthly_commission?.toLocaleString('es-CL') || 0}
                        </td>
                        <td className="py-3 text-gray-500">
                          {ref.created_at ? new Date(ref.created_at).toLocaleDateString('es-CL') : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Commissions history */}
        {data.commissions?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-[#064E3B]">Historial de Comisiones</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="commissions-table">
                  <thead>
                    <tr className="border-b text-left text-gray-500">
                      <th className="pb-2 font-medium">Periodo</th>
                      <th className="pb-2 font-medium">Monto</th>
                      <th className="pb-2 font-medium">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.commissions.map((c, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-3">{c.month}/{c.year}</td>
                        <td className="py-3 font-semibold">${c.amount?.toLocaleString('es-CL')}</td>
                        <td className="py-3">
                          <Badge className={c.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                            {c.status === 'paid' ? 'Pagado' : 'Pendiente'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ReferrerDashboard;
